/*
 * Your Templates — React state over myBrandTemplatesStorage's localStorage CRUD,
 * plus the short-lived toast shown after a save / rename / delete. Mirrors
 * useShareExport's toast-timeout pattern.
 */
import { useCallback, useState } from "react";
import { getErrorMessage } from "../../shared/services/connectorErrors";
import { useTimedToast } from "../../shared/hooks/useTimedToast";
import {
  DuplicateTemplateNameError,
  listMyBrandTemplates,
  logMyBrandTemplatesError,
  removeMyBrandTemplate,
  renameMyBrandTemplate,
  saveMyBrandTemplate,
  TemplateLimitError,
  updateMyBrandTemplate,
  type MyBrandTemplate,
} from "../services/myBrandTemplatesStorage";
import type { BrandSpec } from "../../../types";

const TOAST_DURATION_MS = 3500;
const SAVE_FAILED_MESSAGE = "Couldn't save this template locally.";

export type SaveOutcome = "saved" | "limit" | "duplicate" | "error";
export interface SaveResult {
  readonly outcome: SaveOutcome;
  /** The new entry when `outcome` is "saved". */
  readonly entry?: MyBrandTemplate;
}
export type RenameOutcome = "renamed" | "unchanged" | "duplicate" | "error";

export interface UseMyBrandTemplates {
  readonly templates: readonly MyBrandTemplate[];
  readonly toast: string | null;
  /** Persists `brand` under `name`. Never throws into the caller's click
   *  handler: the outcome says whether it saved, hit the 3-template limit
   *  (EC-22) or failed (an error toast is shown). */
  readonly save: (name: string, brand: BrandSpec, colors: readonly string[]) => SaveResult;
  readonly remove: (id: string) => void;
  readonly rename: (id: string, name: string) => RenameOutcome;
  /** Overwrites a saved template with the latest Customise changes (BR-03). */
  readonly update: (id: string, brand: BrandSpec, colors: readonly string[]) => MyBrandTemplate | null;
  readonly showToast: (message: string) => void;
}

export function useMyBrandTemplates(): UseMyBrandTemplates {
  const [templates, setTemplates] = useState<readonly MyBrandTemplate[]>(() => listMyBrandTemplates());
  const { toast, showToast } = useTimedToast(TOAST_DURATION_MS);

  const save = useCallback((name: string, brand: BrandSpec, colors: readonly string[]): SaveResult => {
    try {
      const entry = saveMyBrandTemplate(name, brand, colors);
      setTemplates((prev) => [entry, ...prev]);
      showToast(`Saved. ${entry.name} is now listed under Templates · Your Templates.`);
      return { outcome: "saved", entry };
    } catch (error) {
      if (error instanceof TemplateLimitError) return { outcome: "limit" };
      if (error instanceof DuplicateTemplateNameError) return { outcome: "duplicate" };
      logMyBrandTemplatesError("my-brand-templates-save-toast", getErrorMessage(error, SAVE_FAILED_MESSAGE));
      showToast(SAVE_FAILED_MESSAGE);
      return { outcome: "error" };
    }
  }, [showToast]);

  const remove = useCallback((id: string) => {
    const removed = templates.find((t) => t.id === id);
    removeMyBrandTemplate(id);
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    if (removed) showToast(`${removed.name} deleted from Your Templates.`);
  }, [templates, showToast]);

  const rename = useCallback((id: string, name: string): RenameOutcome => {
    const prior = templates.find((t) => t.id === id);
    try {
      const updated = renameMyBrandTemplate(id, name);
      if (!updated) return "error";
      // An empty or unchanged name isn't saved (FR-15).
      if (prior?.name === updated.name) return "unchanged";
      setTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
      showToast(`Renamed to ${updated.name}.`);
      return "renamed";
    } catch (error) {
      if (error instanceof DuplicateTemplateNameError) return "duplicate";
      logMyBrandTemplatesError("my-brand-templates-rename-toast", getErrorMessage(error, "Couldn't rename this template."));
      return "error";
    }
  }, [templates, showToast]);

  const update = useCallback((id: string, brand: BrandSpec, colors: readonly string[]) => {
    try {
      const updated = updateMyBrandTemplate(id, brand, colors);
      if (updated) setTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
      return updated;
    } catch (error) {
      logMyBrandTemplatesError("my-brand-templates-update-toast", getErrorMessage(error, SAVE_FAILED_MESSAGE));
      showToast(SAVE_FAILED_MESSAGE);
      return null;
    }
  }, [showToast]);

  return { templates, toast, save, remove, rename, update, showToast };
}
